import { motion } from "framer-motion";
import { MapPin, Phone, Mail } from "lucide-react";
import { FaTiktok } from "react-icons/fa6";
import { FaEnvelope } from "react-icons/fa";

export default function Contact() {
  return (
    <motion.section
      id="contact"
      className="py-24 font-[Poppins] relative overflow-hidden bg-[#0b0b0b]"
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      {/* Soft Light Gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(255,215,0,0.06),transparent_60%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_70%)]"></div>

      <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
        <h2 className="text-5xl font-bold text-yellow-400 mb-6 font-[Playfair_Display] tracking-tight">
          Get in Touch
        </h2>
        <p className="text-gray-300 mb-16 max-w-2xl mx-auto text-lg leading-relaxed">
          Reach out for bookings, inquiries, or to connect with us online.  
          We’re happy to assist anytime.
        </p>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            {
              icon: <MapPin className="w-10 h-10 text-yellow-400" />,
              title: "Address",
              content: "Right across from Agib Mobile, Harar, Ethiopia",
            },
            {
              icon: <Phone className="w-10 h-10 text-yellow-400" />,
              title: "Phone",
              content: (
                <>
                  <p>025 466 8884</p>
                  <p>+251 919 087 201</p>
                  <p>+251 977 707 779</p>
                </>
              ),
            },
            {
              icon: <Mail className="w-10 h-10 text-yellow-400" />,
              title: "Connect",
              content: (
                <div className="flex items-center justify-center gap-6 mt-3">
                  <a
                    href="mailto:ferejhotel@outlook.com"
                    className="text-gray-300 hover:text-yellow-400 transition-transform transform hover:scale-110"
                  >
                    <FaEnvelope size={24} />
                  </a>
                  <a
                    href="https://www.tiktok.com/@ferej_hotel_harar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-yellow-400 transition-transform transform hover:scale-110"
                  >
                    <FaTiktok size={24} />
                  </a>
                </div>
              ),
            },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="bg-[#111111]/70 border border-yellow-500/30 backdrop-blur-md rounded-2xl p-10 text-center text-gray-200 shadow-[0_0_25px_rgba(255,215,0,0.15)] hover:shadow-[0_0_35px_rgba(255,215,0,0.3)] transition-all duration-500 hover:-translate-y-1"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center space-y-3">
                {item.icon}
                <h3 className="text-xl font-semibold text-yellow-400 font-[Playfair_Display]">
                  {item.title}
                </h3>
                <div className="text-gray-300 font-medium leading-relaxed">
                  {item.content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Google Map */}
        <div className="mt-24">
          <h3 className="text-3xl font-semibold text-yellow-400 mb-8 font-[Playfair_Display]">
            Find Us on Google Maps
          </h3>
          <div className="relative w-full h-[420px] rounded-3xl overflow-hidden border border-yellow-500/30 shadow-[0_0_40px_rgba(255,215,0,0.2)]">
            <iframe
              title="Fereja Hotel Harar Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3921.216674930394!2d42.1252291742861!3d9.309180993313084!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1622c2398b0ff72b%3A0x27a7b77eb056ccff!2sFereja%20Hotel!5e0!3m2!1sen!2set!4v1730000000000!5m2!1sen!2set"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-24 border-t border-yellow-900/40 pt-8 text-center bg-black/60 backdrop-blur-md">
        <p className="text-gray-400 text-sm mb-4">
          © {new Date().getFullYear()} Ferej Hotel. All rights reserved.
        </p>
        <a
          href="https://devperportifolio.onrender.com"
          className="inline-block bg-yellow-500 text-black font-semibold px-8 py-3 rounded-full hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-yellow-500/50"
        >
          Designed by DevPer
        </a>
      </footer>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Poppins:wght@400;500;600&display=swap');
        `}
      </style>
    </motion.section>
  );
}
